import clsx from 'clsx';
import { useEffect, useState } from 'react';
import ReactDOM from "react-dom";
import { usePopperTooltip } from 'react-popper-tooltip';
import 'react-popper-tooltip/dist/styles.css';
import { useParams } from 'react-router-dom';
import { useUpdateContactStateMutation } from '../../../../../features/contactsApi';
import { addBannerWithTimeout } from '../../../../lib/centralizedBanner/slice';
import { ContactDTO, ContactType } from "../../../../lib/filters/dto";
import { useAppDispatch } from "../../../../lib/redux/hooks";
import { Spinner } from '../../../../ui/molecules/spinner';
import { PersonDTO } from '../../../database/people/dto';
import { ContactStateEnum, outcomeLabelMap, stateInfoMap } from "../../dto";
import { KoModal } from '../koModal';
import { LeadTakenModal } from '../leadTakenModal';

interface TooltipItemProps {
    contactState: ContactStateEnum;
    contactId: string;
    hoverBgColor: string;
    standardColor: string;
    contactType: ContactType;
    handleUpdateContactState: (newOutcome: ContactStateEnum, newOutcomeInfo: string | null) => void;
    contactToWork?: {
        name: string;
        surname: string;
        phone: string;
        privacy: boolean;
    }
    onOpenTakenModal: () => void;
    onOpenkoModal: () => void;
    setContactToKo: (id?: string) => void;
    onCloseMainTooltip: () => void;
}

function TooltipItem({ contactState, contactId, hoverBgColor, standardColor, handleUpdateContactState, contactType, onOpenTakenModal, onOpenkoModal, setContactToKo, onCloseMainTooltip }: TooltipItemProps) {
    const {
        getTooltipProps: getSubTooltipProps,
        setTooltipRef: setSubTooltipRef,
        setTriggerRef: setSubTriggerRef,
        visible: subVisible,
    } = usePopperTooltip({
        placement: 'right',
        interactive: true,
        trigger: 'hover',
        delayHide: 300,
        closeOnOutsideClick: false,
        closeOnTriggerHidden: false,
    });

    return (
        <>
            <div
                ref={setSubTriggerRef}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimeout(() => {
                        if (contactState === ContactStateEnum.DRAFT || contactState === ContactStateEnum.NORESPONSE) {
                            handleUpdateContactState(contactState, null);
                            onCloseMainTooltip();
                        }
                        if (contactState === ContactStateEnum.LEADTAKEN) {
                            onCloseMainTooltip();
                            onOpenTakenModal();
                        }
                        if (contactState === ContactStateEnum.OPPOSITIONS) {
                            onCloseMainTooltip();
                            onOpenkoModal();
                            setContactToKo(contactId);
                        }
                    }, 0);
                }}
                className={`flex items-center gap-2 h-8 cursor-pointer pr-4 ${hoverBgColor}`}
            >
                <div className={clsx(`h-full w-1.5 block rounded-sm`, standardColor)} />
                <span className='text-text-sm font-medium text-neutral-800'>
                    {outcomeLabelMap.get(contactState) || contactState}
                </span>
            </div>
            {stateInfoMap(contactState).length > 0 && subVisible && ReactDOM.createPortal(
                <div
                    ref={setSubTooltipRef}
                    {...getSubTooltipProps({ className: clsx('tooltip-container', 'bg-brandPrimary-50 p-0 flex flex-col gap-2 py-2 px-1 shadow-xl border border-neutral-200') })}
                >
                    {
                        stateInfoMap(contactState)
                            .filter(state => state !== "Info errate e aggiornate")
                            .filter(state => contactType === ContactType.COMPANY ? state !== 'Registro opposizioni' : contactType === ContactType.PERSON)
                            .map((state, index) => (
                                <div
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onCloseMainTooltip();
                                        setTimeout(() => {
                                            handleUpdateContactState(contactState, state);
                                        }, 0);
                                    }}
                                    key={`${contactId}-${contactState}-${index}`}
                                    className={`flex items-center gap-2 h-8 cursor-pointer pr-4 ${hoverBgColor}`}
                                >
                                    <div className={clsx(`h-full w-1.5 block rounded-sm`, standardColor)} />
                                    <span className='text-text-sm font-medium text-neutral-800'>
                                        {state}
                                    </span>
                                </div>
                            ))}
                </div>,
                document.body
            )}
        </>
    );
}

interface Props {
    contactId: string;
    outcome: ContactStateEnum
    info: string | null;
    noResponseCount?: number | null;
    contact: ContactDTO<ContactType.PERSON> | ContactDTO<ContactType.COMPANY>;
    contactType: ContactType
    width: number;
    listIndex: number
    disabled: boolean;
    contactToWork?: {
        name: string;
        surname: string;
        phone: string;
        privacy: boolean;
    }
}
export function ChangeStatusTooltip({ contactId, outcome, info, contactType, width, listIndex, contactToWork, noResponseCount, contact, disabled }: Props) {
    const [openTakenModal, setOpenTakenModal] = useState(false);
    const [openKoModal, setOpenKoModal] = useState(false);
    const [contactToKo, setContactToKo] = useState<string | undefined>(undefined);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState<{ outcome: ContactStateEnum, info: string | null } | null>(null);

    const {
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
    } = usePopperTooltip(
        {
            placement: 'right',
            interactive: true,
            trigger: 'focus',
            followCursor: false,
            delayHide: 0,
            closeOnTriggerHidden: false,
            closeOnOutsideClick: false,
            visible: tooltipVisible,
        },
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const tooltipElement = document.querySelector('.tooltip-container');
            const triggerElement = document.querySelector(`[data-contact-id="${contactId}"]`);

            if (tooltipVisible &&
                !tooltipElement?.contains(target) &&
                !triggerElement?.contains(target)) {
                setTooltipVisible(false);
            }
        };

        if (tooltipVisible) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [tooltipVisible, contactId]);

    const bgColorMap = new Map<ContactStateEnum, string>([
        [ContactStateEnum.DRAFT, "bg-brandPrimary-200"],
        [ContactStateEnum.LEADTAKEN, "bg-green-200"],
        [ContactStateEnum.TORECALL, "bg-warning-200"],
        [ContactStateEnum.KO, "bg-red-200"],
        [ContactStateEnum.INCORRECT, "bg-neutral-200"],
        [ContactStateEnum.OPPOSITIONS, "bg-red-300"],
        [ContactStateEnum.NORESPONSE, "bg-warning-400"],
    ]);

    const hoverBgColorMap = new Map<ContactStateEnum, string>([
        [ContactStateEnum.DRAFT, "hover:bg-brandPrimary-200"],
        [ContactStateEnum.LEADTAKEN, "hover:bg-green-200"],
        [ContactStateEnum.TORECALL, "hover:bg-warning-200"],
        [ContactStateEnum.KO, "hover:bg-red-200"],
        [ContactStateEnum.INCORRECT, "hover:bg-neutral-200"],
        [ContactStateEnum.OPPOSITIONS, "hover:bg-red-200"],
        [ContactStateEnum.NORESPONSE, "hover:bg-warning-200"],
    ]);

    const textColorMap = new Map<ContactStateEnum, string>([
        [ContactStateEnum.DRAFT, "text-brandPrimary-800"],
        [ContactStateEnum.LEADTAKEN, "text-green-800"],
        [ContactStateEnum.TORECALL, "text-warning-800"],
        [ContactStateEnum.KO, "text-red-800"],
        [ContactStateEnum.INCORRECT, "text-neutral-800"],
        [ContactStateEnum.OPPOSITIONS, "text-red-800"],
        [ContactStateEnum.NORESPONSE, "text-warning-800"],
    ]);

    const standardColorMap = new Map<ContactStateEnum, string>([
        [ContactStateEnum.DRAFT, "bg-brandPrimary-500"],
        [ContactStateEnum.LEADTAKEN, "bg-green-500"],
        [ContactStateEnum.TORECALL, "bg-warning-500"],
        [ContactStateEnum.KO, "bg-red-500"],
        [ContactStateEnum.INCORRECT, "bg-neutral-500"],
        [ContactStateEnum.OPPOSITIONS, "bg-red-800"],
        [ContactStateEnum.NORESPONSE, "bg-warning-700"],
    ]);

    const dispatch = useAppDispatch();
    const { targetId } = useParams()
    const [updateContactState, { isLoading: isUpdatingContactState }] = useUpdateContactStateMutation();

    const displayOutcome = pendingUpdate?.outcome ?? outcome;
    const displayInfo = pendingUpdate?.info ?? info;
    const isUpdating = isUpdatingContactState || pendingUpdate !== null;

    const handleUpdateContactState = async (newOutcome: ContactStateEnum, newOutcomeInfo: string | null) => {
        try {
            setPendingUpdate({ outcome: newOutcome, info: newOutcomeInfo });

            const result = await updateContactState({
                contactId,
                outcome: newOutcome,
                outcomeInfo: newOutcomeInfo,
                contactType,
                targetId: targetId!
            }).unwrap();

            setPendingUpdate(null);
            setOpenTakenModal(false);
            dispatch(addBannerWithTimeout({
                variant: "success",
                message: "Stato del contatto aggiornato con successo.",
            }));

        } catch (error) {
            setPendingUpdate(null);

            const res = error as unknown as { status: number, data: { errors: { message: string }[] } };
            let message = "Si è verificato un errore durante l'aggiornamento dello stato del contatto.";
            const errors = res?.data?.errors ?? [];
            if (errors?.some(e => e.message.includes("has already reached the maximum number of calls"))) {
                message = "Il contatto ha già raggiunto il numero massimo di chiamate consentite.";
            } else if (errors?.some(e => e.message.includes("Contact ID is required"))) {
                message = "Non è possibile prendere il Lead perché non ancora presente in Lista Target. Riprova domani.";
            } else if (errors?.some(e => e.message.includes("Invalid transition from DRAFT to DRAFT"))) {
                message = "Il contatto è già nello stato 'Da Lavorare'";
            }
            dispatch(addBannerWithTimeout({
                variant: "error",
                message: message,
            }));
        }
    }

    return (
        <div
            className={clsx("flex cursor-pointer")} style={{ width: `${width}px` }}>
            <div
                className={clsx(
                    'flex relative flex-col gap-0.5 py-1.5 px-4 justify-center text-center rounded-xl items-center w-full my-2',
                    bgColorMap.get(displayOutcome),
                    textColorMap.get(displayOutcome),
                    isUpdating && 'opacity-75 transition-opacity duration-200'
                )}
                ref={setTriggerRef}
                data-contact-id={contactId}
                onClick={() => !isUpdating && !disabled && setTooltipVisible(!tooltipVisible)}
                style={{ cursor: isUpdating ? 'wait' : 'pointer' }}
            >
                {
                    noResponseCount && displayOutcome === ContactStateEnum.NORESPONSE && !pendingUpdate ?
                        <span className='text-label-sm text-center font-semibold crop-text w-full max-w-full block'>
                            Rec {noResponseCount}
                        </span> :
                        (displayInfo || displayOutcome) &&
                        <span className='text-label-sm text-center font-semibold crop-text w-full max-w-full block'>
                            {displayInfo ?? (outcomeLabelMap.get(displayOutcome) || displayOutcome)}
                        </span>
                }
                {
                    isUpdating &&
                    <div className='absolute inset-0 flex items-center justify-center bg-neutral-400/50 rounded-xl'>
                        <Spinner size={26} />
                    </div>
                }
            </div>
            {tooltipVisible && !isUpdating && ReactDOM.createPortal(
                <div
                    ref={setTooltipRef}
                    {...getTooltipProps({ className: clsx('tooltip-container', 'z-40 bg-brandPrimary-50 p-0 flex flex-col gap-2 py-2 px-1 shadow-xl border border-neutral-200') })}
                >
                    {
                        Object.values(ContactStateEnum).filter(out => contactType === ContactType.COMPANY ? out !== ContactStateEnum.LEADTAKEN : contactType === ContactType.PERSON).map((out, index) => {
                            return (
                                <TooltipItem
                                    handleUpdateContactState={async (newOutcome: ContactStateEnum, newOutcomeInfo: string | null) => {
                                        setTooltipVisible(false);
                                        await handleUpdateContactState(newOutcome, newOutcomeInfo);
                                    }}
                                    contactToWork={contactToWork}
                                    contactType={contactType}
                                    key={index + '-' + contactId + '-' + out}
                                    contactState={out}
                                    contactId={contactId}
                                    hoverBgColor={hoverBgColorMap.get(out) || ''}
                                    standardColor={standardColorMap.get(out) || ''}
                                    onOpenTakenModal={() => {
                                        setOpenTakenModal(true);
                                        setTooltipVisible(false);
                                    }}
                                    onOpenkoModal={() => {
                                        setOpenKoModal(true);
                                        setTooltipVisible(false);
                                    }}
                                    setContactToKo={setContactToKo}
                                    onCloseMainTooltip={() => {
                                        setTooltipVisible(false);
                                    }}
                                />
                            );
                        })
                    }
                </div>,
                document.body
            )}
            <LeadTakenModal
                person={contact.contact as PersonDTO}
                listIndex={listIndex}
                isOpen={openTakenModal}
                onClose={() => setOpenTakenModal(false)}
                isLoading={isUpdatingContactState}
                handleConfirm={(outcome, outcomeInfo) => {
                    handleUpdateContactState(outcome, outcomeInfo);
                }}
            />
            <KoModal
                isLoading={isUpdatingContactState}
                handleConfirm={(outcome, outcomeInfo) => {
                    handleUpdateContactState(outcome, outcomeInfo);
                }}
                isOpen={openKoModal}
                onClose={() => setOpenKoModal(false)}
                contactId={contactToKo}
                listIndex={listIndex}
            />
        </div>
    );
}
